import React from 'react';
import { useEmployees, WPEmployee } from '@gik/about/api/employee';
import { Button, FontIcon, KeepProportions, EmbeddedVideo, Parallax } from '@gik/ui';
import { TeamMemberTile } from '@gik/about/components/TeamMemberTile/TeamMemberTile';
import bemBlock from '@gik/core/utils/bemBlock';
import { getAssetsUrl } from '@gik/core/utils/LinkUtils';
import { useGIKTitle } from '@gik/core/hooks/hooks/useGIKTitle';

export interface AboutProps {
  employees?: WPEmployee[];
  hideWhy?: boolean;
}

// TODO: refactor to use the new components created in the marketing page task
export const About: React.FunctionComponent<AboutProps> = props => {
  const titleEL = useGIKTitle('About us');

  const [employees, advisors] = useEmployees(props.employees);

  const bem = bemBlock('about');

  const pressCoverage = [
    {
      qoute: 'The site puts everything in one place…',
      logo: 'press-seattle-time.png',
    },
    {
      qoute: 'the platform where you can do a lot more than write a check',
      logo: 'press-tc.png',
    },
    {
      qoute: '…a cool site that organizes meals, and a whole lot more.',
      logo: 'press-cool-mom.png',
    },
    {
      qoute: "The Start-Up That Isn't All About the Money",
      logo: 'press-paste.png',
    },
  ];

  const COPY = {
    NORMALIZE_TITLE: "Let's Normalize Support.",
    NORMALIZE_BODY:
      "We get it. We've been there. We've been through situations where everyone asked \"how can I help?\" and we didn't have a good answer. We've had times that we desperately needed help, and didn't know how to ask. And we've felt helpless when our friends and families have gone through something, and we didn't know what to say, or send, or do. Accepting support is hard - but we don't think it should be. We want to make it easy - and normal - to give and receive support.\n",
    WHY_TITLE: 'Why Give InKind?',
    WHY_BODY: 'Learn why a website like Give InKind is needed, and what sets us apart from other support solutions',
    STORY_TITLE: 'Our Story',
    STORY_BODY:
      'Give InKind was started with a notebook, a sea of flowers, and two grieving parents. After experiencing the unexpected stillbirth of their first child, Founder Laura Malcolm and her husband James saw how much work was created for their friends and families that wanted to be supportive from around the country. There were questions about where to order delivery meals from, what to send or say to a family experiencing baby loss, whether or not they were up for visitors or phone calls – and Laura knew there had to be a simpler way to coordinate all of the efforts from the loved ones that wanted to help. In Layla’s honor, Give InKind was born.\n',
    TEAM_TITLE: 'Our Team',
    ADVISORS_TITLE: 'Our Advisors',
    PRESS_TITLE: 'Press Coverage',
    FOOTER_TITLE: 'The Business of Helping Others',
    FOOTER_BODY:
      "Our goal is to allow as many people as possible to access coordinated support, for free, for as long as we can. We make money from the Give InKind products you find on the site, some of our relationships with merchant partners, and from optional tips from Give InKind supporters. We are not a non-profit but we promise we're in this for good.\n",
  };

  // TODO: implement the new bem element modifiers in the html
  // make the why button optional per james his request

  const whyBlock = (
    <div className={bem('why')}>
      <div className={bem('why-content')}>
        <div className={bem('why-title')}>{COPY.WHY_TITLE}</div>
        <div className={bem('why-icon')}>
          <FontIcon name={'chevronRight'} />
        </div>
        <div className={bem('why-body')}>{COPY.WHY_BODY}</div>
      </div>
    </div>
  );

  return (
    <div className={bem()}>
      {titleEL}
      <div className={bem('header', 'top')}>
        <div className={bem('header-text')}>About Us</div>
      </div>
      <div className={bem('normalize')}>
        <div className={bem('section-title', 'primary')}>{COPY.NORMALIZE_TITLE}</div>
        <div className={bem('basic-copy')}>{COPY.NORMALIZE_BODY}</div>
      </div>

      {!props.hideWhy && whyBlock}

      <Parallax
        className={bem('parallax-container')}
        bgImage={`${getAssetsUrl()}about/img/story-head.jpg`}
        strength={200}
      >
        <div className={bem('story')}>
          <div className={bem('section-title', 'pink')}>{COPY.STORY_TITLE}</div>

          <KeepProportions className={bem('video')}>
            <EmbeddedVideo url="https://www.youtube.com/watch?v=RC01N-ZVgWg" />
          </KeepProportions>

          <div className={bem('basic-copy')}>{COPY.STORY_BODY}</div>
        </div>
      </Parallax>

      <div className={bem('team-wrapper')}>
        <div className={bem('team')}>
          <div className={bem('section-title', 'orange')}>{COPY.TEAM_TITLE}</div>
          <div className={bem('team-member-tiles')}>
            {employees?.length > 0 &&
              employees.map(item => (
                <TeamMemberTile
                  key={item.id}
                  photo={item.photo}
                  altPhoto={item.photoAlt}
                  name={item.name}
                  title={item.title}
                  altTitle={item.titleAlt}
                />
              ))}
          </div>
        </div>
      </div>

      <div className={bem('team')}>
        <div className={bem('section-title', 'purple')}>{COPY.ADVISORS_TITLE}</div>
        <div className={bem('team-member-tiles')}>
          {advisors?.length > 0 &&
            advisors.map(item => (
              <TeamMemberTile key={item.id} photo={item.photo} name={item.name} title={item.title} />
            ))}
        </div>
      </div>

      <div className={bem('press-wrapper')}>
        <div className={bem('section-title', 'green')}>{COPY.PRESS_TITLE}</div>
        <div className={bem('press ')}>
          {pressCoverage.map(item => (
            <div className={bem('press-tile')} key={item.logo}>
              <div className={bem('press-quote')}>&quot;{item.qoute}&quot;</div>
              <hr className={bem('press-dots')} />
              <div className={bem('press-logo')}>
                <img src={`${getAssetsUrl()}about/img/${item.logo}`} alt="" />
              </div>
            </div>
          ))}
        </div>

        <div className={bem('press-link')}>
          <Button size="lg" variant="primary" type="a" href="https://create.giveinkind.com/press/">
            VISIT OUR PRESS SITE
          </Button>
        </div>
      </div>

      <div className={bem('footer')}>
        <div className={bem('footer-leaf')}>
          <div className={bem('footer-content')}>
            <div className={bem('footer-title')}>{COPY.FOOTER_TITLE}</div>
            <div className={bem('footer-body')}>{COPY.FOOTER_BODY}</div>
          </div>
        </div>
        <div className={bem('footer-leaf')} />
      </div>
    </div>
  );
};
